import { downloadFileInBrowser } from "utils/utils";

export type DesignScopeImportLogStats = {
  Total?: number;
  Imported?: number;
  Updated?: number;
  Failed?: number;
  Corrected?: number;
  AddedUpdatedWithErrors?: number;
  Unchanged?: number;
  File?: string;
  ActionsPerformed?: {
    inserted?: number;
    updated?: number;
    failed?: number;
  };
};

export type ParsedImportLogComment = {
  stats: DesignScopeImportLogStats | null;
  plainMessage: string | null;
};

export function parseImportLogStatusComment(
  statusComment: string | null | undefined
): ParsedImportLogComment {
  if (!statusComment?.trim()) {
    return { stats: null, plainMessage: null };
  }

  try {
    const parsed = JSON.parse(statusComment);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return {
        stats: parsed as DesignScopeImportLogStats,
        plainMessage: null
      };
    }
  } catch {
    // Plain-text status comments (e.g. seed success messages).
  }

  return { stats: null, plainMessage: statusComment.trim() };
}

export function isImportLogTerminalSuccess(status: string): boolean {
  return status === "PROCESSED" || status === "COMPLETED";
}

export function isImportLogTerminalFailure(status: string): boolean {
  return status === "FAILED";
}

export function importLogHasRowFailures(
  stats: DesignScopeImportLogStats | null
): boolean {
  if (!stats) return false;
  return (
    Number(stats.Failed ?? 0) > 0 ||
    Number(stats.ActionsPerformed?.failed ?? 0) > 0 ||
    Number(stats.AddedUpdatedWithErrors ?? 0) > 0
  );
}

/**
 * Whether the UI should keep the import panel open and show statistics
 * instead of auto-closing on success.
 */
export function shouldShowImportLogSummary(
  status: string,
  stats: DesignScopeImportLogStats | null
): boolean {
  if (isImportLogTerminalFailure(status)) return true;
  if (!isImportLogTerminalSuccess(status)) return false;
  return Boolean(stats) && importLogHasRowFailures(stats);
}

/** Map the synchronous catalog-import body onto the summary the drawer already shows. */
export function catalogImportSummaryStats(result: {
  total: number;
  inserted: number;
  updated: number;
  corrected: number;
  failed: number;
  errorReportUrl: string | null;
}): DesignScopeImportLogStats {
  return {
    Total: result.total,
    Imported: result.inserted,
    Updated: result.updated,
    Corrected: result.corrected,
    Failed: result.failed,
    File: result.errorReportUrl || undefined
  };
}

/** Row failures keep the drawer open on the summary. A clean import closes it. */
export function catalogImportHasRowFailures(result: {
  failed: number;
  errorReportUrl: string | null;
}): boolean {
  return Number(result.failed) > 0 || Boolean(result.errorReportUrl);
}

export function maybeDownloadImportFailureReport(
  stats: DesignScopeImportLogStats | null,
  fileName = "Failed_Design_Scope_Import.csv"
) {
  if (stats?.File) {
    downloadFileInBrowser(stats.File, fileName);
  }
}
